/** @jsxImportSource @emotion/react */

import { useLazyQuery, useMutation } from '@apollo/client'
import { Modal } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { FC, useCallback, useEffect, useState } from 'react'
import InsertLinkIcon from '@mui/icons-material/InsertLink'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { bulkRemoveFlashCard, bulkRemoveFlashCardVariables } from '__generatedTypes__/bulkRemoveFlashCard'
import {
  findAllFlashCard,
  findAllFlashCard_findAllFlashCard_items as IFlashCard,
  findAllFlashCardVariables,
} from '__generatedTypes__/findAllFlashCard'
import { findAllFlashCardGroup_findAllFlashCardGroup_items as IGroup } from '__generatedTypes__/findAllFlashCardGroup'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import Badge from 'components/Badge'
import DeleteModal from 'components/Modals/DeleteModal'
import Table, { TableProps } from 'components/Table'
import TableButtons from 'components/TableButtons/TableButtons'
import client from 'configs/apolloClient'
import { BULK_REMOVE_FLASH_CARDS, BULK_UPDATE_FLASH_CARDS, FIND_ALL_FLASH_CARDS } from 'queries/flashCards'
import { bulkUpdateFlashCard, bulkUpdateFlashCardVariables } from '__generatedTypes__/bulkUpdateFlashCard'
import { toast } from 'utils/toast'
import * as commonClasses from 'components/shared/GroupCards/commonStyles'
import ManageFlashCardsModal from './ManageFlashCardsModal'
import * as classes from './styles'

interface IProps {
  groups: IGroup[]
  initialSelected: IGroup
  selectedSubjectId: string
}

const FlashCardsTabs: FC<IProps> = ({ groups, selectedSubjectId, initialSelected }) => {
  const [getFlashCards, { data: flashCardsData }] = useLazyQuery<findAllFlashCard, findAllFlashCardVariables>(
    FIND_ALL_FLASH_CARDS
  )
  const [flashCards, setFlashCards] = useState<IFlashCard[]>([])
  const [selectedGroup, setSelectedGroup] = useState<IGroup>(initialSelected)
  const [isCardModalOpen, setCardModalOpen] = useState<boolean>(false)
  const [isBulkDeleteModalOpen, setBulkDeleteModalOpen] = useState<boolean>(false)
  const [selectedFlashCard, setSelectedFlashCard] = useState<IFlashCard | null>(null)
  const [flashCardsIdsToDelete, setFlashCardsIdsToDelete] = useState<IFlashCard[]>([])
  const [bulkUpdateFlashCard] = useMutation<bulkUpdateFlashCard, bulkUpdateFlashCardVariables>(BULK_UPDATE_FLASH_CARDS)
  const [bulkRemoveFlashCard] = useMutation<bulkRemoveFlashCard, bulkRemoveFlashCardVariables>(BULK_REMOVE_FLASH_CARDS)

  useEffect(() => {
    setFlashCardsIdsToDelete([])
    selectedGroup &&
      getFlashCards({
        variables: {
          query: {
            take: TAKE_ALL_ITEMS_COUNT,
            where: {
              flashCardGroup: {
                id: selectedGroup.id,
              },
            },
          },
        },
      })
  }, [selectedGroup])

  useEffect(() => {
    flashCardsData && setFlashCards(flashCardsData.findAllFlashCard.items || [])
  }, [flashCardsData])

  useEffect(() => {
    setFlashCards([])
  }, [selectedSubjectId])

  useEffect(() => {
    if (selectedGroup && !groups.find((group) => group.id === selectedGroup.id)) {
      setSelectedGroup(groups[0])
      setFlashCards([])
    }
  }, [groups])

  const handleModalClose = () => {
    setSelectedFlashCard(null)
    setCardModalOpen(false)
  }

  const handleEditClick = (flashCard: IFlashCard) => {
    setSelectedFlashCard(flashCard)
    setCardModalOpen(true)
    setFlashCardsIdsToDelete([])
  }

  const handleBulkDelete = useCallback(async () => {
    try {
      const ids = flashCardsIdsToDelete.map((group) => ({ id: group.id }))
      await bulkRemoveFlashCard({ variables: { ids } })
      setFlashCardsIdsToDelete([])
      setBulkDeleteModalOpen(false)
      client.refetchQueries({
        include: [FIND_ALL_FLASH_CARDS],
      })

      toast.success('Cards deleted successfully')
    } catch (error) {
      toast.error('Failed to delete cards')
    }
  }, [flashCardsIdsToDelete])

  const FlashCardsColumn: TableProps<IFlashCard>['columns'] = [
    {
      title: 'Title',
      renderCell: (note) => (
        <Typography css={commonClasses.longText} variant="body2" data-testid="card-title">
          {note.name}
        </Typography>
      ),
      flex: '0 1 300px',
    },
    {
      title: 'Description',
      renderCell: (note) => (
        <Typography css={commonClasses.longText} variant="body2">
          {note.description}
        </Typography>
      ),
      flex: '0 1 300px',
    },
    {
      title: 'Group',
      renderCell: (note) => (
        <Badge text={note.flashCardGroup.title} color={note.flashCardGroup.color} css={classes.badge} />
      ),
      flex: '0 1 250px',
    },
    {
      title: 'URL',
      renderCell: (note) => (
        <IconButton size="small" href={note.link} target="_blank" css={classes.link}>
          <InsertLinkIcon />
        </IconButton>
      ),
      flex: '0 1 100px',
    },
    {
      renderCell: (note) => (
        <EditIcon css={commonClasses.editIcon} onClick={() => handleEditClick(note)} data-testid="edit-card-btn" />
      ),
      flex: '0 1 29px',
    },
  ]

  const onSort = useCallback(async (items: IFlashCard[]) => {
    try {
      if (items.length > 0) {
        setFlashCards(items)

        await bulkUpdateFlashCard({
          variables: {
            bulkUpdateFlashCardInput: items.map(({ id }, order) => ({ id, order })),
          },
        })

        toast.success('Cards order updated successfully')
      }
    } catch (error) {
      toast.error('Failed to update cards order')
    }
  }, [])

  return (
    <div>
      <Typography css={classes.title} variant="title3" component="p">
        Cards
      </Typography>
      <div css={classes.tabsBlock}>
        {groups.map((group) => (
          <Typography
            onClick={() => setSelectedGroup(group)}
            key={group.id}
            css={[classes.tab, selectedGroup?.id === group.id && classes.activeTab]}
            variant="body2"
          >
            {group.title}
          </Typography>
        ))}
      </div>
      <Table
        columns={FlashCardsColumn}
        items={flashCards}
        isSelectable
        selectedItems={flashCardsIdsToDelete}
        onSelect={setFlashCardsIdsToDelete}
        onSort={onSort}
        isSortable
      />
      {selectedGroup && (
        <TableButtons
          itemName="Card"
          areItemsToDelete={flashCardsIdsToDelete.length !== 0}
          onAddItem={() => setCardModalOpen(true)}
          onCancel={() => setFlashCardsIdsToDelete([])}
          onBatchDelete={() => setBulkDeleteModalOpen(true)}
        />
      )}
      {selectedGroup !== null && (
        <Modal open={isCardModalOpen} onClose={handleModalClose}>
          <ManageFlashCardsModal
            onClose={handleModalClose}
            flashCardGroups={groups}
            flashCard={selectedFlashCard ?? { id: 'id', flashCardGroup: { id: selectedGroup.id } }}
            flashCardsLength={flashCards.length}
            isEditMode={!!selectedFlashCard}
          />
        </Modal>
      )}
      <DeleteModal
        open={isBulkDeleteModalOpen}
        count={flashCardsIdsToDelete.length}
        onClose={() => setBulkDeleteModalOpen(false)}
        onConfirm={handleBulkDelete}
      />
    </div>
  )
}

export default FlashCardsTabs
